import React, {lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
// react-tabs ships the base rules every tab strip in the app builds on: the tab list's
// bottom border (recoloured inline per strip), the -1px offset that merges the selected
// tab into it, and `display:none` for unselected panels. Several routes using react-tabs
// are lazy chunks, so importing it from any one of them would leave the others unstyled
// until that chunk happened to load — it belongs here, before App.css so the app's own
// rules still win.
import 'react-tabs/style/react-tabs.css';
// The brand palette as CSS variables, remapped under data-genre="scifi" / "horror". Before App.css,
// which reads them.
import './theme/genre.css';
import './theme/lightMode.css';
import './App.css';
import 'bootstrap/dist/css/bootstrap.css'
import '/node_modules/react-grid-layout/css/styles.css'
import '/node_modules/react-resizable/css/styles.css'
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {Routes, Route, Navigate, useLocation, useNavigate} from 'react-router-dom';
import {PRESENTATION_PATH, needsSessionHandoff, requestSessionHandoff, signInAndReturn} from "./utilities/presentationChannel";
import SideBar from "./components/SideBar";
import Header from "./components/Header";
import LoginForm from "./components/LoginForm";
import SignupForm from "./components/SignupForm";
import {SIGNUP_ENABLED} from "./utilities/Constants";
import VerifyEmail from "./components/VerifyEmail";
import CancelDeletion from "./components/CancelDeletion";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import {alpha, createTheme, getContrastRatio, ThemeProvider} from "@mui/material/styles";
import {ThemeProvider as AppThemeProvider, useLightMode} from "./context/ThemeContext";
import {themeHex} from "./utilities/themeColors";
import {HelpProvider} from "./context/HelpContext";
import {FeatureFlagProvider, useFeature, useFeatureFlags} from "./context/FeatureFlagContext";
import HelpOverlay from "./components/LazyHelpOverlay";
import {WebSocketProvider} from "./utilities/WebSocketProvider";
import {TransformWrapper} from "react-zoom-pan-pinch";
import {CircularProgress, CssBaseline} from "@mui/material";
import ToolBar from "./components/ToolBar";
import NumberInput from "./components/NumberInput";
import Invite from "./components/Invite";
import JoinPage from "./components/JoinPage";
import {useIdleTimeout} from "./hooks/useIdleTimeout";
import IdleTimeoutModal from "./components/IdleTimeoutModal";
import TermsOfServiceModal from "./components/TermsOfServiceModal";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import TermsOfUsePage from "./components/TermsOfUsePage";
import {announceLogout} from "./utilities/WebSocketProvider";
import SessionStartNotification from "./utilities/SessionStartNotification";
import {clearSageHistory, getErrorMessage} from "./utilities/Utils";
import {Analytics} from '@vercel/analytics/react';

// Route-level code splitting: these pull in heavy per-feature deps (Babylon.js dice
// physics, three.js tavern scene, large data modules) that every logged-in user would
// otherwise load on login regardless of which routes they visit.
const GameMasterScreen = lazy(() => import("./components/GameMasterScreen"));
const Party = lazy(() => import("./components/Party"));
const Gear = lazy(() => import("./components/Gear"));
const Tavern = lazy(() => import("./components/Tavern"));
const TableTop = lazy(() => import("./components/TableTop"));
const TableTopPresentation = lazy(() => import("./components/TableTopPresentation"));
const CharacterSheetPage = lazy(() => import("./components/CharacterSheetPage"));
const TableTopChatPage = lazy(() => import("./components/TableTopChatPage"));
const QuickReferencePage = lazy(() => import("./components/QuickReferencePage"));
const Sage = lazy(() => import("./components/Sage"));
const Bestiary = lazy(() => import("./components/Bestiary"));
const Hoard = lazy(() => import("./components/Hoard"));
const Billing = lazy(() => import("./components/Billing"));
const AdminUsers = lazy(() => import("./components/AdminUsers"));
const AdminStorage = lazy(() => import("./components/AdminStorage"));

// How far past each viewport edge the tabletop map can be panned, as a fraction of the
// viewport size (0.5 = half a screen of empty table beyond the map on every side).
const TABLETOP_PAN_MARGIN = 0.5;

function RouteFallback() {
    return (
        <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%", padding: "48px 0"}}>
            <CircularProgress/>
        </div>
    );
}

// Presentation Mode opens without an opener (its own thread, see presentationChannel.js), so
// it starts signed out: fetch the opening tab's session, then reload so everything that reads
// sessionStorage at mount (theme, the WS provider, AppInner) starts signed in as before.
// Rendered in place of the signed-out routes, which would otherwise bounce it to the login.
function PresentationSessionGate() {
    const [failed, setFailed] = useState(() => !needsSessionHandoff());
    useEffect(() => {
        if (failed) return;
        requestSessionHandoff().then(ok => ok ? window.location.reload() : setFailed(true));
    }, [failed]);
    if (!failed) return <RouteFallback/>;
    // Not opened by a tabletop tab - typically a table screen run in a second browser (see
    // startPresentationHeartbeat for why), which signs in for itself and comes back here.
    return (
        <div style={{position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center', justifyContent: 'center',
            color: 'var(--th-9c6fd6)', fontSize: 28, fontWeight: 600, textAlign: 'center', padding: '0 24px'}}>
            <div>Sign in to use this window as the table screen.</div>
            <button onClick={signInAndReturn} style={{
                fontSize: 20, padding: '12px 28px', borderRadius: 8, cursor: 'pointer', color: '#F5F5F5',
                background: 'var(--th-5c3d9c)', border: '1px solid var(--th-9c6fd6)',
            }}>
                Sign in
            </button>
        </div>
    );
}

// Sage is a Dragonlord-tier feature (super admins included). Hiding the nav entries
// isn't enough on its own — a bookmarked or hand-typed /sage has to bounce too.
function SageRoute() {
    const sageEnabled = useFeature('SAGE');
    const {loaded} = useFeatureFlags();
    // Don't redirect an entitled user's deep link out from under them just because
    // the entitlement fetch hasn't answered yet.
    if (!loaded && !sageEnabled) return <RouteFallback/>;
    return sageEnabled ? <Sage/> : <Navigate to="/tavern" replace/>;
}

function AppInner() {
    let userJSON = JSON.parse(sessionStorage.getItem('user'));
    const navOpen = (userJSON) ? (localStorage.getItem("navOpen_" + userJSON.username) || "true") : "true";
    const [open, setOpen] = useState(navOpen === 'true');
    const location = useLocation();
    const navigate = useNavigate();
    const {lightMode, showTooltips, genre} = useLightMode();

    const handleIdle = useCallback(() => {
        if (!sessionStorage.getItem('user')) return;
        announceLogout();
        sessionStorage.removeItem("user");
        clearSageHistory();
        navigate('/');
    }, [navigate]);

    const { showWarning, remainingSeconds, extendSession } = useIdleTimeout(handleIdle);

    // Blocks the whole app until the user accepts the current Terms of Service —
    // covers both first login and re-prompting after AuthController#CURRENT_TOS_VERSION
    // is bumped, since this runs on every authenticated mount (incl. LoginForm's
    // post-login reload).
    const [tosGateOpen, setTosGateOpen] = useState(false);
    const [tosBusy, setTosBusy] = useState(false);

    useEffect(() => {
        if (!userJSON) return;
        fetch(`${process.env.REACT_APP_API_PROXY}/auth/tosStatus`, {
            headers: { Authorization: `Bearer ${userJSON.accessToken}` }
        })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => setTosGateOpen(!data.accepted))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAcceptTos = async () => {
        setTosBusy(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/acceptTos`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${userJSON.accessToken}` }
            });
            if (!res.ok) throw new Error(await getErrorMessage(res));
            setTosGateOpen(false);
        } catch (err) {
            // leave the gate open so the user can retry
        } finally {
            setTosBusy(false);
        }
    };

    const whiteBase = '#6C4F9B';
    const whiteMain = alpha(whiteBase, 0.7);

    const violetBase = '#6C4F9B';
    const violetMain = alpha(violetBase, 0.7);

    // const theme = createTheme({
    //     palette: {
    //         type: 'dark',
    //         primary: {
    //             main: '#A54CFF',
    //         },
    //         secondary: {
    //             main: '#f50057',
    //         },
    //         background: {
    //             default: '#30303050',
    //             paper: '#424242',
    //         },
    //         text: {
    //             primary: '#fff',
    //             secondary: '#f50057',
    //             disabled: '#303030',
    //             hint: '#fff',
    //         },
    //         action: {
    //             selected: '#6444B4',
    //         },
    //     },
    // });

    const darkTheme = createTheme({
        zIndex: {
            // MUI tooltips are portalled and otherwise cap out at 1500, which now renders
            // behind Header.js's fixed navbar (zIndex 2147483647) whenever a tooltip
            // overlaps it — e.g. the side tab panel's top-row tooltips.
            tooltip: 2147483647,
        },
        // Real hex rather than the CSS theme variables: MUI derives hover, contrast and
        // ripple colors from these, and it can only do that math on a color it can read.
        palette: {
            mode: lightMode ? 'light' : 'dark',
            primary: {
                main: themeHex(lightMode ? '#5B3F8A' : '#6C4F9B', genre),
            },
            action: {
                selected: themeHex(lightMode ? '#7B5FB5' : '#6C4F9B', genre),
            },
            background: lightMode ? {
                default: themeHex('#C0B8DC', genre),
                paper: themeHex('#BCB4D6', genre),
            } : {},
            // Light mode's surfaces are pale lavender, so MUI's own text (inputs, menus,
            // list items, dialogs) has to be the dark ink App.css uses for --text-primary.
            text: lightMode ? {
                primary: themeHex('#1A1225', genre),
                secondary: alpha(themeHex('#1A1225', genre), 0.7),
            } : {},
        },
        components: {
            MuiTooltip: {
                defaultProps: {
                    disableHoverListener: !showTooltips,
                    disableFocusListener: !showTooltips,
                    disableTouchListener: !showTooltips,
                },
            },
        },
    });


    // create a darkTheme function to handle dark theme using createTheme
    // const darkTheme = createTheme({
    //     palette: {
    //         mode: false ? 'dark' : 'light',
    //     },
    // });

    //this removes the context menu of the browser; this prevents confusion when panning with the right mouse button
    useEffect(() => {
        const handleContextmenu = e => {
            e.preventDefault()
        }
        document.addEventListener('contextmenu', handleContextmenu)
        return function cleanup() {
            document.removeEventListener('contextmenu', handleContextmenu)
        }
    }, [])

    const transformWrapper = useRef(null);
    const [transformWrapperDisabled, setTransformWrapperDisabled] = useState(false);
    const initPositionX = (userJSON) ? (localStorage.getItem("initPositionX_" + userJSON.username) || 0) : 0;
    const [mapPositionX, setMapPositionX] = useState(initPositionX)
    const initPositionY = (userJSON) ? (localStorage.getItem("initPositionY_" + userJSON.username) || 0) : 0;
    const [mapPositionY, setMapPositionY] = useState(initPositionY)
    const initScale = (userJSON) ? (localStorage.getItem("initScale_" + userJSON.username) || 1) : 1;
    const [mapScale, setMapScale] = useState(initScale);

    // The library's own limitToBounds pins the map edges to the viewport edges, so at
    // scale 1 (map exactly fills the container) there's no room to pan at all. It's
    // turned off below and replaced with this looser clamp: the map may be dragged up to
    // TABLETOP_PAN_MARGIN of the viewport past each edge, never further. Pinning the
    // transform here holds it at the limit while dragging — the library recomputes each
    // pan move from the drag's start point, so it doesn't creep past on later moves.
    //
    // Only user gestures (pan / wheel / pinch) are clamped, never onTransformed: that also
    // fires for the programmatic camera restore when returning to the tabletop, and at that
    // point the map image hasn't loaded, so the content measures tiny and the clamp dragged
    // the restored view back to the corner — which then got saved as the camera.
    function clampPanToMargin(ref) {
        const { wrapperComponent, contentComponent, transformState: state } = ref.instance ?? {};
        if (!wrapperComponent || !contentComponent || !state) return;
        const clampAxis = (pos, wrapperSize, contentSize) => {
            const margin = wrapperSize * TABLETOP_PAN_MARGIN;
            const overflow = wrapperSize - contentSize * state.scale;
            const min = Math.min(0, overflow) - margin;
            const max = Math.max(0, overflow) + margin;
            return Math.min(max, Math.max(min, pos));
        };
        const x = clampAxis(state.positionX, wrapperComponent.offsetWidth, contentComponent.offsetWidth);
        const y = clampAxis(state.positionY, wrapperComponent.offsetHeight, contentComponent.offsetHeight);
        if (Math.abs(x - state.positionX) > 0.5 || Math.abs(y - state.positionY) > 0.5) {
            ref.setTransform(x, y, state.scale, 0);
        }
    }

    function handleTransform(ref, state) {
        setMapPositionX(Math.round(state.positionX));
        setMapPositionY(Math.round(state.positionY));
        setMapScale(state.scale);
    }

    useEffect(() => {
        if (userJSON) {
            localStorage.setItem("initPositionX_" + userJSON.username, mapPositionX);
            localStorage.setItem("initPositionY_" + userJSON.username, mapPositionY);
            localStorage.setItem("initScale_" + userJSON.username, mapScale);
        }
    }, [mapPositionX, mapPositionY, mapScale])

    if (!userJSON) {
        if (location.pathname === PRESENTATION_PATH) return <PresentationSessionGate/>;
        return (
            <div>
                <Routes>
                    <Route exact="true" path="/" element={<LoginForm/>}/>
                    <Route path="/signup" element={SIGNUP_ENABLED ? <SignupForm/> : <Navigate to="/" replace/>}/>
                    <Route path="/verify-email" element={<VerifyEmail/>}/>
                    <Route path="/cancel-deletion" element={<CancelDeletion/>}/>
                    <Route path="/forgot-password" element={<ForgotPassword/>}/>
                    <Route path="/reset-password" element={<ResetPassword/>}/>
                    <Route path="/join/:inviteCode" element={<JoinPage/>}/>
                    <Route path="/privacy" element={<PrivacyPolicyPage/>}/>
                    <Route path="/terms" element={<TermsOfUsePage/>}/>
                    <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
            </div>
        )
    }

    // Bare read-only "projector" view — deliberately outside the Header/SideBar chrome
    // below (opened in its own tab, dragged to a second monitor, see MapsTree.js's
    // "Presentation Mode" button). It brings its own WebSocketProvider since this branch
    // never mounts the main authenticated tree's provider.
    if (location.pathname === PRESENTATION_PATH) {
        return (
            <WebSocketProvider>
                <Suspense fallback={<RouteFallback/>}>
                    <TableTopPresentation/>
                </Suspense>
            </WebSocketProvider>
        );
    }

    // The tabletop's Game Log & Chat on its own tab (SideTabPanel's pop-out button). Bare
    // like the presentation view, with its own WebSocketProvider for the same reason.
    if (location.pathname === '/tabletop/chat') {
        return (
            <WebSocketProvider>
                <ThemeProvider theme={darkTheme}>
                    <Suspense fallback={<RouteFallback/>}>
                        <TableTopChatPage/>
                    </Suspense>
                </ThemeProvider>
            </WebSocketProvider>
        );
    }

    // The tabletop's Quick Reference on its own tab (SideTabPanel's pop-out button). Static
    // reference content, so no WebSocketProvider.
    if (location.pathname === '/tabletop/reference') {
        return (
            <ThemeProvider theme={darkTheme}>
                <Suspense fallback={<RouteFallback/>}>
                    <QuickReferencePage/>
                </Suspense>
            </ThemeProvider>
        );
    }

    // A single character sheet on its own, opened in a new browser tab from the sheet's
    // action bar (see CharacterHeader). Bare like the presentation view above: no Header
    // or SideBar, since the point is a sheet a player can park on a second monitor. No
    // WebSocketProvider either — the sheet only ever talks to the REST API.
    if (location.pathname.startsWith('/character/')) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Suspense fallback={<RouteFallback/>}>
                    <Routes>
                        <Route path="/character/:characterId" element={<CharacterSheetPage/>}/>
                    </Routes>
                </Suspense>
            </ThemeProvider>
        );
    }

    return (
        <>
        <IdleTimeoutModal
            open={showWarning}
            remainingSeconds={remainingSeconds}
            onStay={extendSession}
            onLogout={handleIdle}
        />
        <TermsOfServiceModal
            show={tosGateOpen}
            busy={tosBusy}
            onAccept={handleAcceptTos}
            onDecline={handleIdle}
        />
        <WebSocketProvider>
            <SessionStartNotification />
            <ThemeProvider theme={darkTheme}>
                <div className="app">
                    <DndProvider backend={HTML5Backend}>
                        <div className="main">
                            <div style={{minHeight: "60px"}}>
                                <Header/>
                            </div>
                            <HelpOverlay/>
                            <div className="main-content">
                                {location.pathname !== '/tavern' && <SideBar open={open} setOpen={setOpen}/>}
                                <Suspense fallback={<RouteFallback/>}>
                                <Routes>
                                    <Route exact="true" path="/" element={<LoginForm/>}/>
                                    <Route path="/signup" element={SIGNUP_ENABLED ? <SignupForm/> : <Navigate to="/" replace/>}/>
                                    <Route path="/invite" element={<Invite/>}/>
                                    <Route path="/join/:inviteCode" element={<JoinPage/>}/>
                                    <Route path="/privacy" element={<PrivacyPolicyPage/>}/>
                                    <Route path="/terms" element={<TermsOfUsePage/>}/>
                                    <Route path="/cancel-deletion" element={<CancelDeletion/>}/>
                                    <Route path="/tavern" element={<Tavern/>}/>
                                    <Route path="/gmscreen" element={<GameMasterScreen/>}/>
                                    <Route path="/party" element={<Party/>}/>
                                    <Route path="/gear" element={<Gear/>}/>
                                    <Route path="/sage" element={<SageRoute/>}/>
                                    <Route path="/bestiary" element={<Bestiary/>}/>
                                    <Route path="/hoard" element={<Hoard/>}/>
                                    {/* Also the return target for Stripe Checkout and the
                                        Billing Portal — StripeBillingService builds its
                                        success/cancel/return URLs against this path, so
                                        renaming it strands users coming back from Stripe. */}
                                    <Route path="/billing" element={<Billing/>}/>
                                    {/* Super admins only; the page checks with the server itself. */}
                                    <Route path="/admin/users" element={<AdminUsers/>}/>
                                    <Route path="/admin/storage" element={<AdminStorage/>}/>
                                    <Route path="/tabletop" element={
                                        <TransformWrapper ref={transformWrapper}
                                                          initialScale={parseFloat(initScale) || 1}
                                                          initialPositionX={parseFloat(initPositionX) || 0}
                                                          initialPositionY={parseFloat(initPositionY) || 0}
                                                          disabled={transformWrapperDisabled}
                                                          doubleClick={{disabled: true}}
                                                          limitToBounds={false}
                                                          onTransformed={handleTransform}
                                                          onPanning={clampPanToMargin}
                                                          onWheel={clampPanToMargin}
                                                          onPinching={clampPanToMargin}
                                                          panning={{
                                                              allowLeftClickPan: false,
                                                              allowMiddleClickPan: false
                                                          }}>
                                            <TableTop id="table-top" open={open} mapScale={mapScale} transformWrapperRef={transformWrapper}/>
                                        </TransformWrapper>
                                    }/>
                                </Routes>
                                </Suspense>
                            </div>
                        </div>
                    </DndProvider>
                </div>
            </ThemeProvider>
        </WebSocketProvider>
        </>
    );
}

function App() {
    return (
        <AppThemeProvider>
            <HelpProvider>
                <FeatureFlagProvider>
                    <AppInner/>
                </FeatureFlagProvider>
            </HelpProvider>
            <Analytics />
        </AppThemeProvider>
    );
}

export default App;

